import type { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import { Section } from "../view/section";
import { FormCheckbox, FormField, FormInput, FormTextarea } from "./form-field";
import type { InsuranceFormData } from "./types";

interface RemainingProps {
  register: UseFormRegister<InsuranceFormData>;
  watch: UseFormWatch<InsuranceFormData>;
  setValue: UseFormSetValue<InsuranceFormData>;
  signatureUrl: string | null;
  valuerName: string | null;
  supersedesId: number | null;
  supersedesAddress: string | null;
  supersededById: number | null;
  supersededByAddress: string | null;
  isEdit: boolean;
}

export function SectionFslForm({ register }: { register: UseFormRegister<InsuranceFormData> }): React.ReactElement {
  return (
    <Section id="fsl" title="Fire and Emergency Levy Statement">
      <FormTextarea
        id="fslText"
        rows={4}
        placeholder="The Depreciated Replacement Cost estimate has been assessed for Fire and Emergency levy calculation purposes only..."
        {...register("fslText")}
      />
    </Section>
  );
}

export function SectionNotesForm({ register }: { register: UseFormRegister<InsuranceFormData> }): React.ReactElement {
  return (
    <Section id="notes" title="Notes">
      <FormTextarea id="note" rows={4} placeholder="Free-form notes..." {...register("note")} />
    </Section>
  );
}

export function SectionSignatureForm({
  signatureUrl,
  valuerName,
}: {
  signatureUrl: string | null;
  valuerName: string | null;
}): React.ReactElement {
  return (
    <Section id="signature" title="Valuer Signature">
      {signatureUrl ? (
        <div className="h-24 w-48 rounded-md border bg-muted flex items-center justify-center text-sm text-muted-foreground">
          Signature: {valuerName}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No signature on file for {valuerName ?? "selected valuer"}.{" "}
          <Link to="/settings/valuers" className="text-primary hover:underline">
            Upload via valuer management
          </Link>
          .
        </p>
      )}
    </Section>
  );
}

export function SectionHousekeepingForm({
  register,
  watch,
  setValue,
}: {
  register: UseFormRegister<InsuranceFormData>;
  watch: UseFormWatch<InsuranceFormData>;
  setValue: UseFormSetValue<InsuranceFormData>;
}): React.ReactElement {
  const cancelled = watch("cancelled") as boolean;

  return (
    <Section id="housekeeping" title="Housekeeping">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <FormCheckbox
            id="excludeFromMailout"
            label="Exclude from Mailout"
            checked={watch("excludeFromMailout") as boolean}
            onChange={(v) => setValue("excludeFromMailout", v)}
          />
          <FormCheckbox
            id="isActive"
            label="Active"
            checked={watch("isActive") as boolean}
            onChange={(v) => setValue("isActive", v)}
          />
          <FormCheckbox
            id="cancelled"
            label="Cancelled"
            checked={cancelled}
            onChange={(v) => {
              setValue("cancelled", v);
              if (v) setValue("cancelledDate", new Date().toISOString().split("T")[0] ?? "");
            }}
          />
          {cancelled && (
            <FormField label="Cancelled Date" htmlFor="cancelledDate">
              <FormInput id="cancelledDate" type="date" {...register("cancelledDate")} />
            </FormField>
          )}
          <FormCheckbox
            id="finalSentConfirmed"
            label="Final Sent Confirmed"
            checked={watch("finalSentConfirmed") as boolean}
            onChange={(v) => setValue("finalSentConfirmed", v)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Owner" htmlFor="owner" className="col-span-2">
            <FormInput id="owner" {...register("owner")} />
          </FormField>
          <FormField label="Owner Address" htmlFor="ownerAddress" className="col-span-2">
            <FormInput id="ownerAddress" {...register("ownerAddress")} />
          </FormField>
          <FormField label="Broker" htmlFor="broker" className="col-span-2">
            <FormInput id="broker" {...register("broker")} />
          </FormField>
          <FormField label="Broker Address" htmlFor="brokerAddress" className="col-span-2">
            <FormInput id="brokerAddress" {...register("brokerAddress")} />
          </FormField>
        </div>
      </div>
    </Section>
  );
}

export function SectionSupersedingForm({
  isEdit,
  supersedesId,
  supersedesAddress,
  supersededById,
  supersededByAddress,
}: {
  isEdit: boolean;
  supersedesId: number | null;
  supersedesAddress: string | null;
  supersededById: number | null;
  supersededByAddress: string | null;
}): React.ReactElement | null {
  if (!isEdit) return null;

  return (
    <Section id="superseding" title="Superseding">
      <div className="space-y-2">
        {supersedesId && (
          <p className="text-sm">
            This record supersedes{" "}
            <Link to={`/insurance/${supersedesId}`} className="text-primary hover:underline font-medium">
              {supersedesId} – {supersedesAddress}
            </Link>
          </p>
        )}
        {supersededById && (
          <p className="text-sm">
            Superseded by{" "}
            <Link to={`/insurance/${supersededById}`} className="text-primary hover:underline font-medium">
              {supersededById} – {supersededByAddress}
            </Link>
          </p>
        )}
        {!supersedesId && !supersededById && (
          <p className="text-sm text-muted-foreground">This record has no superseding relationships.</p>
        )}
      </div>
    </Section>
  );
}
